import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import {
  avatarOptions,
  getFullName,
  shortenName,
} from 'scenes/ProjectFormV2/components/PersonalInformation/PersonalInformation';
import SupporterIcon from './Icons/SupporterIcon';
import CheckMarkModal from '../Modals/CheckMarkModal';
import { isMobile } from '../../../../../utils';

const organizationTypeOptions = [
  { value: 'mosque', label: 'Moschee' },
  { value: 'community', label: 'Gemeinde' },
  { value: 'foundation', label: 'Stiftung' },
  { value: 'religious_organization', label: 'Religiöse Organisation' },
  { value: 'aid_organization', label: 'Hilfsorganisation' },
  { value: 'educational_organization', label: 'Bildungsorganisation' },
  { value: 'youth_organization', label: 'Jugendorganisation' },
  { value: 'cultural_organization', label: 'Kulturorganisation' },
  { value: 'sports_organization', label: 'Sportorganisation' },
  { value: 'social_organization', label: 'Soziale Organisation' },
  { value: 'student_organization', label: 'Studentenorganisation' },
  { value: 'initiative', label: 'Initiative' },
  { value: 'network', label: 'Netzwerk' },
  { value: 'other_organization', label: 'Sonstige Organisation' },
];

export const getAvatarBoxDisplayName = (project) =>
  project?.visibility === 'anonymous'
    ? 'Anonymer Projektinitiator'
    : project?.visibility === 'verified'
      ? shortenName(project?.initiator_first_name, project?.initiator_last_name)
      : getFullName(project?.initiator_first_name, project?.initiator_last_name) ||
        'Projektinitiator';

export const hasProjectOrganization = (project) =>
  Boolean(
    project?.organization_name &&
      project.organization_name !== '' &&
      project?.organization_type &&
      project.organization_type !== ''
  );

export const hasCompletedProjectPersonalization = (project) =>
  Boolean(project?.is_personalization_done ?? project?.isPersonalizationDone);

export const getAvatarBoxTitle = (project) => {
  if (!hasCompletedProjectPersonalization(project)) {
    return null;
  }

  return hasProjectOrganization(project)
    ? project.organization_name
    : getAvatarBoxDisplayName(project);
};

const AvatarBox = ({
  project,
  isEvent,
  isDonationPage,
  size = 40,
  badgeSize = 15,
  titleSize = 14,
  titleLineHeight = 16,
  subtitleSize = 14,
  subtitleLineHeight = 16,
}) => {
  // Legacy projects did not have the initiator personalization step. Do not
  // invent an avatar or placeholder copy until the owner saves that step.
  if (!hasCompletedProjectPersonalization(project)) {
    return null;
  }

  const hasOrganization = hasProjectOrganization(project);

  const avatar = avatarOptions.find(
    (item) => item.baseKey === project?.selected_avatar
  );

  const organizationTypeLabel =
    organizationTypeOptions.find(
      (item) => item.value === project?.organization_type
    )?.label || 'Organisationstyp';

  const initiatorCity = project?.city ?? '';

  let title = getAvatarBoxTitle(project);

  if(isEvent && isMobile() && isDonationPage){
    title = `Organisiert von ${title}`;
  }


  const subtitle = hasOrganization
    ? organizationTypeLabel
    : initiatorCity
      ? `aus ${initiatorCity}`
      : 'Privatperson';

  const showOrganizationLogo = hasOrganization && project?.organization_logo;
  const showSelectedAvatar = !hasOrganization && project?.selected_avatar;

  return (
    <OwnerRow>
      <AvatarWrap $size={size}>
        {showOrganizationLogo ? (
          <LogoImage
            src={project.organization_logo}
            alt={project.organization_name}
          />
        ) : showSelectedAvatar ? (
          <PreviewAvatar $bg={avatar?.bg}>
            {avatar ? (
              <AvatarInner $color={avatar.color} $size={size}>
                {avatar.icon}
              </AvatarInner>
            ) : (
              <SupporterIcon />
            )}
          </PreviewAvatar>
        ) : (
          <SupporterIcon />
        )}

        {project.checkmark_enabled && (
          <CheckMarkModal
            badgeSize={badgeSize}
            hasOrganization={hasOrganization}
          />
        )}
        {/*<LogoBadge $size={badgeSize}>*/}
        {/*  {hasOrganization ? <BlueCheck /> : <ReceiptCheck />}*/}
        {/*</LogoBadge>*/}
      </AvatarWrap>

      <OwnerTexts>
        {(isEvent && isMobile() && isDonationPage) ? (
          <MobileAndEvent>{title}</MobileAndEvent>
        ) : (
          <OwnerTitle $fontSize={titleSize} $lineHeight={titleLineHeight}>
            {title}
          </OwnerTitle>
        )}

        {!isDonationPage && (
          <OwnerSubtitle
            $fontSize={subtitleSize}
            $lineHeight={subtitleLineHeight}
          >
            {subtitle}
          </OwnerSubtitle>
        )}
      </OwnerTexts>
    </OwnerRow>
  );
};

AvatarBox.propTypes = {
  project: PropTypes.object.isRequired,
  size: PropTypes.number,
  badgeSize: PropTypes.number,
  titleSize: PropTypes.number,
  titleLineHeight: PropTypes.number,
  subtitleSize: PropTypes.number,
  subtitleLineHeight: PropTypes.number,
};

export default AvatarBox;

const OwnerRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const AvatarWrap = styled.div`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 9999px;
  background: #f5f5f5;
  border: 1px solid rgba(0, 0, 0, 0.08);
  position: relative;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewAvatar = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 9999px;
  background: ${({ $bg }) => $bg || '#f5f5f5'};
  display: flex;
  align-items: center;
  justify-content: center;
  border: ${({ $active }) =>
    $active ? '2px solid #669F2A' : '1px solid #E5E7EB'};
  background: ${({ $bg }) => $bg || '#fff'};
  color: ${({ $color }) => $color || '#111'};
`;

const AvatarInner = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => Math.max($size - 12, 20)}px;
  height: ${({ $size }) => Math.max($size - 12, 20)}px;
  border-radius: 9999px;
  color: ${({ $color }) => $color};
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 9999px;
`;


const OwnerTexts = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`;

const OwnerTitle = styled.div`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: ${({ $lineHeight }) => $lineHeight}px;
  color: #181d27;
  margin-bottom:0;
  text-align: left;
`;

const OwnerSubtitle = styled.div`
  margin-top:0;
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: ${({ $lineHeight }) => $lineHeight}px;
  color: #535862;
`;

const MobileAndEvent = styled.div`
  /* Heading *
/* Text sm/Regular */
font-family: 'Inter';
font-style: normal;
font-weight: 400;
font-size: 14px;
line-height: 20px;
/* identical to box height, or 143% */

color: #181D27;

 `
